// The unit an explainer tells its story about. Every name here is fictional.
// Pages pass a sector unit (see pages/industries/data.ts) or any HeroUnit from ../heroUnits (structurally
// compatible); without one the explainers tell the Halvard heat pump story (heroUnits[0]).
import { heroUnits } from '../heroUnits';

export type PartnerRole = 'dealer' | 'installer' | 'retailer' | 'service agent' | 'service partner' | 'distributor';

export type ExplainerUnit = {
  /** Fictional brand, as printed on the plate (shown upper-case). */
  brand: string;
  model: string;
  /** What it is, e.g. "Round baler". */
  kind: string;
  serial: string;
  /** Who sits between you and the owner. */
  partner: string;
  partnerRole: PartnerRole;
  owner: string;
  /** Second owner, for the transfer story. */
  owner2: string;
  /** Four or five handover / commissioning checks. */
  checks: string[];
  /** The answer the unit page gives (to `question`). */
  answer: string;
  /** Where the answer came from, e.g. "Operator manual p.42". */
  source: string;
  /** Part named in the claim. */
  part: string;
  /* -------- optional detail (sensible fallbacks are derived) -------- */
  question?: string; // the owner's question
  handoverName?: string; // HeroUnit name for the handover form (alias of `form`)
  shipTo?: string; // ship-to on the packing slip, default partner
  title?: string; // record title, default `${model} ${kind}`
  lot?: string;
  section?: string; // manual heading the answer came from
  excerpt?: string; // highlighted line in the manual
  issue?: string; // claim title
  parts?: [string, string, string][]; // ERP rows after the unit: name, serial, lot
  history?: [string, string][]; // record history: label, date
  form?: string; // handover form title
  signLabel?: string; // who signs
  signer?: string;
  outcome?: [string, string]; // success screen, two lines
  statusRow?: [string, string, string]; // record row: key, before, after
  doneNote?: string; // e.g. "PDI 5 of 5 · signed"
  serviceNote?: string; // claim service line
  since?: string; // claim warranty line
};

export type Unit = Required<ExplainerUnit> & { Role: string; /** Model without the brand prefix, for plates. */ plateModel: string; /** Short owner names for tight spots. */ ownerShort: string; owner2Short: string };

/** The agri baler story (the original default). */
export const AGRI_UNIT: ExplainerUnit = {
  brand: 'Harrowfield',
  model: 'HB-1200',
  kind: 'Round baler',
  serial: 'HB12-26-00418',
  lot: 'L2609-14',
  partner: 'Moorside Agri',
  partnerRole: 'dealer',
  owner: 'Hill Farm',
  owner2: 'Ridge Contracting',
  checks: ['Hydraulics checked', 'PTO guard fitted', 'Tyre pressures set', 'Safety decals in place', 'Operator walkthrough'],
  question: 'Which oil does the gearbox take, and how much?',
  answer: '80W-90 gear oil, 2.4 litres. Check the level every 50 hours at the plug on the left side.',
  source: 'Operator manual p.42',
  section: '7.3 Gearbox oil',
  excerpt: 'Use 80W-90 gear oil, 2.4 L. Check every 50 hours.',
  part: 'Twine arm assembly',
  issue: 'Twine arm not returning',
  parts: [
    ['Pickup reel kit', 'PRK-26-1182', 'L2609-03'],
    ['Twine arm assembly', 'TA-26-0921', 'L2608-77'],
    ['Net wrap unit', 'NWU-26-0310', 'L2609-08'],
  ],
  history: [
    ['Handover · Moorside Agri', 'MAR 2026'],
    ['Service · 50 hours', 'MAY 2026'],
    ['Service · 250 hours', 'SEP 2026'],
  ],
  form: 'Pre-delivery inspection',
  doneNote: 'PDI 5 of 5 · signed',
};

/** Default: heroUnits[0], the Halvard air source heat pump, commissioned by an installer, plus explainer detail. */
export const DEFAULT_UNIT: ExplainerUnit = {
  ...heroUnits[0],
  question: 'What flow temperature should I use with radiators?',
  title: 'Halvard AW-9 heat pump',
  lot: 'L2609-21',
  signLabel: 'Homeowner signature',
  doneNote: 'Commissioned · signed',
  section: '5.2 Flow temperature',
  excerpt: 'Radiators: set the flow temperature to 45°C.',
  issue: 'Outdoor unit fan not starting',
  parts: [
    ['Fan motor assembly', 'KFM-26-1182', 'L2609-03'],
    ['Diverter valve', 'KDV-26-0921', 'L2608-77'],
    ['Wall controller', 'KWC-26-0310', 'L2609-08'],
  ],
  history: [
    ['Commissioned', 'MAR 2026'],
    ['Controls update', 'MAY 2026'],
    ['Annual service', 'SEP 2026'],
  ],
  serviceNote: '2 visits, on schedule',
  since: 'Active since commissioning',
};

const FORM: Record<PartnerRole, string> = {
  dealer: 'Pre-delivery inspection',
  installer: 'Commissioning',
  retailer: 'Setup and registration',
  'service agent': 'Installation check',
  'service partner': 'Installation sign-off',
  distributor: 'Delivery check',
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Fill the optional fields so scenes can read everything directly. */
/** "The new owners, 14 Orchard Lane" → "The new owners" (only when long). */
export const shortName = (s: string, max = 20) => (s.length > max ? s.split(/,|\s\(/)[0].trim() : s);

/** Fill the optional fields so scenes can read everything directly. */
export function resolveUnit(u: ExplainerUnit = DEFAULT_UNIT): Unit {
  const lot = u.lot ?? `L${u.serial.replace(/\D/g, '').slice(0, 4)}-${u.serial.slice(-2)}`;
  const form = u.form ?? u.handoverName ?? FORM[u.partnerRole];
  const prefix = u.serial.split('-')[0];
  const yy = (u.serial.match(/-(\d{2})-/) || [])[1] ?? '26';
  const history = u.history ?? [
    [form, 'MAR 2026'],
    ['Service visit', 'SEP 2026'],
  ];
  const candidate = `${u.model} ${u.model.startsWith(u.brand) ? u.kind.toLowerCase() : u.kind}`;
  return {
    ...u,
    Role: cap(u.partnerRole),
    plateModel: u.model.startsWith(u.brand + ' ') ? u.model.slice(u.brand.length + 1) : u.model,
    ownerShort: shortName(u.owner),
    owner2Short: shortName(u.owner2),
    question: u.question ?? 'How do I keep it running well?',
    handoverName: form,
    shipTo: u.shipTo ?? u.partner,
    title: u.title ?? (candidate.length <= 22 ? candidate : u.model),
    lot,
    section: u.section ?? 'Care and maintenance',
    excerpt: u.excerpt ?? u.answer,
    issue: u.issue ?? `${u.part}: fault reported`,
    parts: u.parts ?? [
      [u.part, `${prefix}-P1-${yy}`, lot],
      ['Installation kit', `${prefix}-IK-${yy}`, lot],
      ['Manual pack', `${prefix}-DOC-${yy}`, lot],
    ],
    history,
    form,
    signLabel: u.signLabel ?? 'Owner signature',
    signer: u.signer ?? u.owner,
    outcome: u.outcome ?? ['Warranty active', 'from today'],
    statusRow: u.statusRow ?? ['Warranty', 'Not started', 'Active from today'],
    doneNote: u.doneNote ?? `${form} · signed`,
    serviceNote: u.serviceNote ?? `${Math.max(history.length - 1, 0)} ${history.length - 1 === 1 ? 'service' : 'services'}, on schedule`,
    since: u.since ?? `Active since ${form.toLowerCase()}`,
  };
}

/** Mono serial size that still fits a fixed column (stage px). */
export const fitMono = (s: string, width: number, base: number) => Math.min(base, +(width / (s.length * 0.62)).toFixed(1));
